import { ErrorHandler, Injectable, inject } from '@angular/core';
import {
  addOtherCalendarRequest,
  ApiService,
  AssistResourceBM,
  cacheGet,
  deleteOtherCalendarRequest,
  eventBounds,
  eventStatuses,
  OtherCalendarListItem,
  otherCalendars,
  PostPutOtherCalendarListItem,
  searchAdminUsers,
  SearchedAdminUserModel,
  searchOtherCalendarRequest,
  updateOtherCalendarRequest,
} from '@cue/api';
import { concatMap, filter, map, Observable, of, switchMap, throwError } from 'rxjs';
import {
  CalendarAttendee,
  CalendarAvailability,
  CalendarEvent,
  CalendarEventDatetime,
  CalendarEventRecurrence,
  CalendarLocation,
  CalendarOrganizer,
  CalendarSchedule,
  CalendarScheduleItem,
  CalendarsService,
  ExtendedCalendarScheduleItem,
} from '@cue/calendars';
import {
  generateAvailability,
  getPatchedAvailabilityWithEvent,
  getResourceAttendees,
  getResourceTimeZone,
  getCalendarSyncTimelineEventRange,
  getTemporalResourceAttendees,
  getTextKeyAndClass,
  patchAvailabilityByRestrictions,
  patchAvailabilityByWorkingHours,
  tryToFindLocation,
} from '../utils';
import { add, differenceInMinutes } from 'date-fns';
import { ConfigService } from './config.service';
import {
  AnonymousAssistEvent,
  AppState,
  AssistEventType,
  FaultyAssistEvent,
  FilteredResources,
  IdentifiedAssistEvent,
  isIdentifiedAssistEvent,
  LimitedAssistEvent,
  ReservationFilters,
  Schedule,
  TimelineIdentifiedAssistEvent,
} from '../models';

import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AssistErrorHandler } from './error-handler';
import { remove } from 'remove-accents';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, mergeMap } from 'rxjs/operators';
import { localized } from '@cue/translate';
import { currentUser } from '../selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CalendarSyncCalendarEvent,
  CalendarSyncLoadEventsResponse,
  CalendarSyncResourceEvents,
  CalendarSyncService,
} from '@cue/calendar-sync';
import { CalendarSyncCalendarsServiceResolver } from './calendar-sync-calendars-service-resolver.service';

type ConfirmStatusRequest = { email: string; iCalUid: string; id?: string };
type ExtendedCalendarSchedule = Omit<CalendarSchedule, 'scheduleItems'> & {
  scheduleItems: ExtendedCalendarScheduleItem[];
};

type TimelineScheduleAvailability = {
  email: string;
  availabilityView: string;
  serviceError?: unknown;
};

function isExtendedCalendarSchedule(
  schedule: CalendarSchedule | CalendarSchedule<ExtendedCalendarScheduleItem>
): schedule is ExtendedCalendarSchedule {
  return schedule.scheduleItems.every((item) => 'attendees' in item && 'locations' in item);
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private translocoService = inject(TranslocoService);
  private apiService = inject(ApiService);
  private configService = inject(ConfigService);
  private store = inject<Store<AppState>>(Store<AppState>);
  private errorHandler: AssistErrorHandler;
  private calendarsService = inject(CalendarsService);
  private calendarSyncService = inject(CalendarSyncService);
  private calendarSyncCalendarsServiceResolver = inject(CalendarSyncCalendarsServiceResolver);

  constructor() {
    const eHandler = inject(ErrorHandler);

    this.errorHandler = eHandler as AssistErrorHandler;
  }

  getEventBounds(email: string, icalUid: string): Observable<{ start: Date; end: Date } | null> {
    return this.apiService
      .call(
        eventBounds({
          email: email,
          iCalUid: icalUid,
        })
      )
      .pipe(catchError(() => of(null)));
  }

  private getCachedSchedules(filter: {
    fromDate?: Date;
    toDate?: Date;
    uniqueMails: any[];
    filteredRooms: FilteredResources[];
  }) {
    return this.apiService.call(cacheGet(filter.fromDate, filter.toDate, filter.uniqueMails)).pipe(
      map((serverSchedules) => {
        return filter.filteredRooms.map((fr) => {
          return serverSchedules
            .filter((x) => (fr.resources as AssistResourceBM[]).find((res) => res.email === x.scheduleId) != null)
            .map((x: any) => {
              const originalResource = (fr.resources as AssistResourceBM[]).find((res) => res.email === x.scheduleId);
              if (x.error != null) {
                return {
                  scheduleId: x.scheduleId,
                  scheduleItems: [],
                  schedulingSettings: originalResource.schedulingSettings,
                  workingHours: originalResource.workingHours,
                  availabilityView: new Array(
                    Math.floor(differenceInMinutes(filter.toDate!, filter.fromDate!) / 15) + 1
                  )
                    .map((_) => CalendarAvailability.limited)
                    .join(''),
                  requirementId: fr.requirement.id,
                  additional: fr.additional,
                  resourceId: originalResource!.id,
                  serviceError: x.error?.message != null ? x.error.message : x.error?.responsiveService,
                };
              }

              return {
                ...x,
                scheduleItems: x.scheduleItems ?? [],
                schedulingSettings: originalResource.schedulingSettings,
                requirementId: fr.requirement.id,
                additional: fr.additional,
                resourceId: originalResource!.id,
              };
            });
        });
      })
    );
  }

  private filterEventsBySettings(events: IdentifiedAssistEvent[], settings: ReservationFilters, resources: any[]) {
    let checker = (arr: any[], target: any[]) => target.every((v) => arr.includes(v));

    return events.filter((e) => {
      const canceledEventFilter = settings.filters.showCanceled || !e.cancelled;
      const states = settings.filters.state;
      const attendeesMails = e.attendees.map((x) => x.emailAddress?.address?.toUpperCase()).filter((x) => !!x);

      const statesFiltered =
        states.length === 0 ||
        e.resourceAttendees.some((x) => {
          const statusTextEtc = getTextKeyAndClass(x);
          const statusText = statusTextEtc[2];
          return states.some((x) => x.value.status === statusText);
        });

      const normalizedResourceNames = e.resourceAttendees.map((x) => remove(x.resource.name ?? '').toLowerCase());
      const normalizedResourceDisplayNames = e.resourceAttendees.map((x) =>
        remove(localized(x.resource.displayName, this.translocoService) ?? '').toLowerCase()
      );
      const normalizedResourceDisplayNamesForApp = e.resourceAttendees.map((x) =>
        remove(localized(x.resource.displayNameForApp, this.translocoService) ?? '').toLowerCase()
      );

      const hasResources = e.resourceAttendees.length > 0;

      const filteredByName =
        settings.resourceNames.length == 0 ||
        settings.resourceNames.some((filtrName) => {
          const normalizedFilterName = remove(filtrName ?? '').toLowerCase();
          return (
            normalizedResourceNames.includes(normalizedFilterName) ||
            normalizedResourceDisplayNames.includes(normalizedFilterName) ||
            normalizedResourceDisplayNamesForApp.includes(normalizedFilterName)
          );
        });

      const settingsAttendeesMails = settings.attendees.map((x: any) => x.value).filter((x: any) => x != null);

      const attendeesOrganizerFiltered =
        settings.attendees.length == 0 ||
        settings.attendees.some(
          (m) => !m.isGroup && e.organizer?.emailAddress.address?.toUpperCase() == m.value?.toUpperCase()
        ) ||
        checker(
          attendeesMails.map((x) => x.toUpperCase()),
          settingsAttendeesMails.map((x: string) => x.toUpperCase())
        );

      return canceledEventFilter && hasResources && filteredByName && attendeesOrganizerFiltered && statesFiltered;
    });
  }

  private onlyUnique(value: any, index: number, self: any[]) {
    return self.indexOf(value) === index;
  }

  private getConfirmStatuses(requests: ConfirmStatusRequest[]) {
    return this.apiService.call(eventStatuses(requests));
  }

  getTimelinePatchedScheduleForResource(
    resource: AssistResourceBM,
    fromDate: Date,
    toDate: Date,
    existingEvent?: {
      start: Date;
      end: Date;
    }
  ) {
    const availabilityView$ = this.configService.value.cachingEnabled
      ? this.apiService
          .call(cacheGet(fromDate, toDate, [resource.email]))
          .pipe(map((schedules) => schedules[0].availabilityView.split('') as CalendarAvailability[]))
      : this.calendarsService
          .getScheduleForEmails([resource.email], fromDate, toDate)
          .pipe(map((schedules) => schedules[0].availabilityView.split('') as CalendarAvailability[]));

    return availabilityView$.pipe(
      map((availabilityView) =>
        this.patchTimelineAvailabilityForResource(resource, fromDate, availabilityView, existingEvent)
      )
    );
  }

  getTimelinePatchedSchedulesForResources(
    resources: AssistResourceBM[],
    fromDate: Date,
    toDate: Date,
    existingEventsByResourceId: ReadonlyMap<number, { start: Date; end: Date }> = new Map()
  ): Observable<Map<number, CalendarAvailability[] | null>> {
    if (resources.length === 0) {
      return of(new Map());
    }

    const emails = Array.from(new Set(resources.map((resource) => resource.email)));
    const schedules$: Observable<TimelineScheduleAvailability[]> = this.configService.value.cachingEnabled
      ? this.apiService.call(cacheGet(fromDate, toDate, emails)).pipe(
          map((schedules: any[]) =>
            schedules.map((schedule) => ({
              email: schedule.scheduleId,
              availabilityView: schedule.availabilityView,
              serviceError: schedule.error ?? schedule.serviceError,
            }))
          )
        )
      : this.calendarsService.getScheduleForEmails(emails, fromDate, toDate).pipe(
          map((schedules) =>
            schedules.map((schedule) => ({
              email: schedule.email,
              availabilityView: schedule.availabilityView,
              serviceError: schedule.serviceError,
            }))
          )
        );

    return schedules$.pipe(
      map((schedules) => {
        const schedulesByEmail = new Map(
          schedules.map((schedule) => [schedule.email.trim().toLowerCase(), schedule] as const)
        );

        return new Map<number, CalendarAvailability[] | null>(
          resources.map((resource) => {
            const schedule = schedulesByEmail.get(resource.email.trim().toLowerCase());

            if (!schedule?.availabilityView || schedule.serviceError != null) {
              return [resource.idForFiltering, null];
            }

            try {
              return [
                resource.idForFiltering,
                this.patchTimelineAvailabilityForResource(
                  resource,
                  fromDate,
                  schedule.availabilityView.split('') as CalendarAvailability[],
                  existingEventsByResourceId.get(resource.idForFiltering)
                ),
              ];
            } catch {
              return [resource.idForFiltering, null];
            }
          })
        );
      })
    );
  }

  private patchTimelineAvailabilityForResource(
    resource: AssistResourceBM,
    fromDate: Date,
    availabilityView: CalendarAvailability[],
    existingEvent?: { start: Date; end: Date }
  ): CalendarAvailability[] {
    const resourceTimezone = getResourceTimeZone(resource.workingHours);
    const availabilityWithWorkingHours = patchAvailabilityByWorkingHours(
      fromDate,
      availabilityView,
      resource.workingHours.workingHoursSetting,
      resourceTimezone,
      resource.schedulingSettings
    );
    const availabilityWithRestrictions = patchAvailabilityByRestrictions(
      resourceTimezone,
      fromDate,
      new Date(),
      availabilityWithWorkingHours,
      resource.schedulingSettings,
      resource.conditions.reservedTo
    );

    return existingEvent
      ? getPatchedAvailabilityWithEvent(availabilityWithRestrictions, fromDate, existingEvent.start, existingEvent.end)
      : availabilityWithRestrictions;
  }

  // FREE / BUSY
  getScheduleForAttendee(email: string, fromDate: Date, toDate: Date) {
    const errorView = generateAvailability(fromDate, toDate, CalendarAvailability.tentative);
    return this.calendarsService
      .getScheduleForEmails([email], fromDate, toDate)
      .pipe(
        map((x) =>
          x[0].availabilityView == null || x[0].availabilityView.length === 0
            ? errorView.join('')
            : x[0].availabilityView
        )
      );
  }

  // FREE / BUSY
  getScheduleForAttendees(
    attendeeEmails: string[],
    fromDate: Date,
    toDate: Date
  ): Observable<{ email: string; availabilityView: string; items: CalendarScheduleItem[] }[]> {
    const errorView = generateAvailability(fromDate, toDate, CalendarAvailability.tentative);

    if (attendeeEmails.length === 0) {
      return of([]);
    }
    return this.calendarsService.getScheduleForEmails(attendeeEmails, fromDate, toDate).pipe(
      map((x) =>
        x.map((a) => ({
          email: a.email,
          availabilityView:
            a.availabilityView == null || a.availabilityView.length === 0 ? errorView.join('') : a.availabilityView,
          items: a.scheduleItems,
        }))
      )
    );
  }

  public searchAdminUsers(search: string): Observable<SearchedAdminUserModel[]> {
    return this.apiService.call(searchAdminUsers(search)).pipe(catchError(() => of([])));
  }

  private getCalendarEventsFromIdentifiedAssistEvents(
    calendarEvents: CalendarEvent[],
    resources: AssistResourceBM[]
  ): Observable<IdentifiedAssistEvent[]> {
    return of(calendarEvents).pipe(
      map((events) => {
        if (this.configService.isGspace()) {
          events.forEach((event) => {
            const location = tryToFindLocation(
              event,
              resources,
              this.translocoService.getActiveLang(),
              this.translocoService.getDefaultLang()
            );
            event.locations =
              location != null
                ? [location]
                : [
                    {
                      displayName: 'N/A',
                      locationType: 'N/A',
                      locationUri: 'N/A',
                      uniqueId: 'N/A',
                      uniqueIdType: 'N/A',
                    },
                  ];
          });
        }
        return events;
      }),
      switchMap((events) => {
        const request = events
          .map((e: any) => {
            const resourceAttendess = getTemporalResourceAttendees(
              e,
              resources,
              this.translocoService.getActiveLang(),
              this.translocoService.getDefaultLang()
            );
            return {
              event: e,
              resourceAttendess: resourceAttendess,
            };
          })
          .filter((e) => {
            return e.resourceAttendess.length > 0;
          })
          .map((r) => {
            return r.resourceAttendess.map((x) => ({
              email: x.attendee.emailAddress.address,
              iCalUid: r.event.iCalUId,
              id: r.event.id,
            }));
          });

        const flattenRequest = request.reduce((acc, val) => acc.concat(val), []);
        return this.getConfirmStatuses(flattenRequest).pipe(map((response) => [events, response] as const));
      }),
      map(([events, response]) => {
        return events.map((e) => {
          try {
            if (e.iCalUId === null || e.iCalUId === '' || e.iCalUId === undefined) {
              throw new Error('Missing iCalUId, or it is empty');
            }

            const temporalResourceAttendees = getTemporalResourceAttendees(
              e,
              resources,
              this.translocoService.getActiveLang(),
              this.translocoService.getDefaultLang()
            );
            if (temporalResourceAttendees.length === 0) {
              return {
                originalEvent: e,
                logged: new Date(),
                assistEventType: AssistEventType.Anonymous,
                reason: 'Cannot find event attendee from location',
              } as AnonymousAssistEvent;
            }

            const resourceAttendees = getResourceAttendees(temporalResourceAttendees, e, response);

            const finished = resourceAttendees.some((x) => x.confirmationMetadata.finished);
            const afterEnd = e.end.utcDateTime < new Date();
            const showConfirmButton = !e.cancelled && resourceAttendees.some((x) => x.confirmAvailable) && !afterEnd;

            const canEditFromColleague = !this.configService.isTouchoneCalendar()
              ? false
              : (e.visibility.toLowerCase() == 'public' && e.eventPermissions?.canEditParentEventPublic) ||
                (e.visibility.toLowerCase() == 'private' && e.eventPermissions?.canEditParentEventPrivate) ||
                (e.visibility.toLowerCase() === 'public' && e.eventPermissions?.canEditPublic) ||
                (e.visibility.toLowerCase() === 'private' && e.eventPermissions?.canEditPrivate);

            const canViewFromColleague = this.configService.isTouchoneCalendar()
              ? false
              : (e.visibility.toLowerCase() === 'public' && e.eventPermissions?.canReadPublic) ||
                (e.visibility.toLowerCase() === 'private' && e.eventPermissions?.canReadPrivate);

            // show change button zobrazit pokud
            const showChangeButton = !finished && (e.isOrganizer || canEditFromColleague) && !afterEnd;

            // show finish button zobrazit pokud
            const showFinishButton =
              !finished && e.isOrganizer && !e.cancelled && !afterEnd && e.start.utcDateTime <= new Date();

            const showDeleteButton = !finished && e.isOrganizer && !e.cancelled && !showFinishButton && !afterEnd;

            const meAttending = e.attendees.some(
              (x) => x.emailAddress?.address?.toLowerCase() === this.currentUserEmailLowerCased()
            );

            const identifiedEvent: IdentifiedAssistEvent = {
              finished: finished,
              meAttending: meAttending,
              resourceAttendees: resourceAttendees,
              id: e.id,
              assistEventType: AssistEventType.Identified,
              end: e.end,
              start: e.start,
              organizer: e.organizer,
              visibility: e.visibility,
              subject: e.subject,
              showAs: e.showAs,
              status: e.status,
              isAllDay: e.isAllDay,
              attendees: e.attendees.map((a) => {
                const foundInResourceAttendees = resourceAttendees.find(
                  (rA) => rA.attendee.emailAddress?.address.toUpperCase() === a.emailAddress?.address.toUpperCase()
                );
                if (foundInResourceAttendees) {
                  a.resource = true;
                }
                return a;
              }),
              isOrganizer: e.isOrganizer,
              bodyHtml: e.bodyHtml,
              reminder: e.reminder,
              bodyPreview: e.bodyPreview,
              locations: e.locations,
              type: e.type,
              onlineMeetingProvider: e.onlineMeetingProvider,
              onlineMeetingUrl:
                e.onlineMeetingUrl ??
                response.find((status) => status.iCalUid === e.iCalUId && (status.id === e.id || !status.id))
                  ?.onlineMeetingUrl,
              ownerEventId: e.ownerEventId,
              ownerUserId: e.ownerUserId,
              seriesMasterId: e.seriesMasterId,
              occurrenceId: e.occurrenceId,
              recurrence: e.recurrence,
              eventPermissions: e.eventPermissions,
              canEditFromColleague: canEditFromColleague,
              canViewFromColleague: canViewFromColleague,
              cancelled: e.cancelled,
              iCalUId: e.iCalUId,
              afterEnd: afterEnd,
              showConfirmButton: showConfirmButton,
              showChangeButton: showChangeButton,
              showFinishButton: showFinishButton,
              showDeleteButton: showDeleteButton,
            };
            return identifiedEvent;
          } catch (error: any) {
            // Proste se to nepovedlo
            return {
              error: error?.message ?? error,
              logged: new Date(),
              assistEventType: AssistEventType.Faulty,
              originalEvent: e,
            } as FaultyAssistEvent;
          }
        });
      }),
      switchMap((y) => {
        const toLog = y.filter((e) => !isIdentifiedAssistEvent(e));
        return this.errorHandler.writeEventsLog(toLog).pipe(map(() => y.filter(isIdentifiedAssistEvent)));
      })
    );
  }

  getSchedulesForResources(filteredRooms: FilteredResources[], fromDate: Date, toDate: Date): Observable<Schedule[][]> {
    const emails: string[] = [];

    const allResourcesData = filteredRooms.reduce((acc, fr) => {
      const toAdd = fr.resources as AssistResourceBM[];
      return [...acc, ...toAdd];
    }, []);

    filteredRooms.forEach((frooms) => {
      const toAdd = (frooms.resources as AssistResourceBM[]).map((x) => x.email);
      emails.push(...toAdd);
    });

    if (this.configService.value.cachingEnabled && this.configService.getMode() !== 'touchone-calendar') {
      const uniqueMails = emails.filter(this.onlyUnique);
      return this.getCachedSchedules({
        fromDate: fromDate,
        toDate: toDate,
        uniqueMails: uniqueMails,
        filteredRooms: filteredRooms,
      });
    }

    return this.calendarsService.getScheduleForEmails(emails, fromDate, toDate).pipe(
      switchMap((calendarSchedules) => {
        let statuses = of([]);
        if (this.configService.isTouchoneCalendar()) {
          const scheduleList: (CalendarSchedule | CalendarSchedule<ExtendedCalendarScheduleItem>)[] = [
            ...calendarSchedules,
          ];
          const touchoneCalendarSchedules = scheduleList.filter(isExtendedCalendarSchedule);
          const requests = touchoneCalendarSchedules.reduce<ConfirmStatusRequest[]>(
            (acc, cs) => [
              ...acc,
              ...cs.scheduleItems.reduce<ConfirmStatusRequest[]>((acc, it) => {
                const resourceAttendess = getTemporalResourceAttendees(
                  it,
                  allResourcesData,
                  this.translocoService.getActiveLang(),
                  this.translocoService.getDefaultLang()
                );

                const toAdd = {
                  email: cs.email,
                  iCalUid: it.iCalUId,
                  id: it.id,
                };

                const toAddFromScheduleItems = resourceAttendess.map((rA) => ({
                  email: rA.resource.email,
                  iCalUid: it.iCalUId,
                  id: it.id,
                }));

                return [...acc, toAdd, ...toAddFromScheduleItems];
              }, []),
            ],
            []
          );

          statuses = this.getConfirmStatuses(requests);
        }

        return statuses.pipe(
          map((statuses) => ({
            statuses,
            calendarSchedules,
          }))
        );
      }),
      map(({ calendarSchedules, statuses }) => {
        return filteredRooms.map((fr) => {
          return (
            calendarSchedules
              .filter((cs) => (fr.resources as AssistResourceBM[]).find((res) => res.email == cs.email) != null)
              // unique by email
              .filter((value, index, self) => self.map((x) => x.email).indexOf(value.email) === index)
              .map((cs) => {
                const originalResource = (fr.resources as AssistResourceBM[]).find((res) => res.email === cs.email);
                const data: Schedule = {
                  serviceError: cs.serviceError,
                  additional: false,
                  availabilityView: cs.availabilityView,
                  schedulingSettings: originalResource.schedulingSettings,
                  resourceId: originalResource.id,
                  scheduleId: cs.email,
                  scheduleItems: this.configService.isTouchoneCalendar()
                    ? cs.scheduleItems
                        .map((origSi) => {
                          try {
                            const si = origSi as ExtendedCalendarScheduleItem;

                            const temporalResourceAttendees = getTemporalResourceAttendees(
                              si,
                              allResourcesData,
                              this.translocoService.getActiveLang(),
                              this.translocoService.getDefaultLang()
                            );

                            if (temporalResourceAttendees.length === 0) {
                              return null;
                            }

                            const resourceAttendees = getResourceAttendees(
                              temporalResourceAttendees,
                              {
                                ...si,
                                start: CalendarEventDatetime.fromBrowserDatetime(si.start),
                                end: CalendarEventDatetime.fromBrowserDatetime(si.end),
                              },
                              statuses
                            );

                            const anyResourceAttendeesFinished = resourceAttendees.some(
                              (x) => x.confirmationMetadata.finished
                            );

                            const afterEnd = CalendarEventDatetime.fromBrowserDatetime(si.end).utcDateTime < new Date();

                            // show confirm button zobrazit pokud
                            const showConfirmButton =
                              si.isOrganizer && resourceAttendees.some((x) => x.confirmAvailable) && !afterEnd;

                            const canEditFromColleague = !this.configService.isTouchoneCalendar()
                              ? false
                              : (si.visibility.toLowerCase() == 'public' &&
                                  si.eventPermissions?.canEditParentEventPublic) ||
                                (si.visibility.toLowerCase() == 'private' &&
                                  si.eventPermissions?.canEditParentEventPrivate) ||
                                (si.visibility.toLowerCase() === 'public' && si.eventPermissions?.canEditPublic) ||
                                (si.visibility.toLowerCase() === 'private' && si.eventPermissions?.canEditPrivate);

                            const canViewFromColleague = this.configService.isTouchoneCalendar()
                              ? false
                              : (si.visibility.toLowerCase() === 'public' && si.eventPermissions?.canReadPublic) ||
                                (si.visibility.toLowerCase() === 'private' && si.eventPermissions?.canReadPrivate);

                            // show change button zobrazit pokud
                            const showChangeButton =
                              !anyResourceAttendeesFinished &&
                              !si.cancelled &&
                              (si.isOrganizer || canEditFromColleague) &&
                              !afterEnd;

                            // show finish button zobrazit pokud
                            const showFinishButton =
                              !anyResourceAttendeesFinished &&
                              si.isOrganizer &&
                              !si.cancelled &&
                              !afterEnd &&
                              si.start.utcDateTime <= new Date();

                            const showDeleteButton =
                              !anyResourceAttendeesFinished &&
                              si.isOrganizer &&
                              !si.cancelled &&
                              !showFinishButton &&
                              !afterEnd;

                            const meAttending = si.attendees.some(
                              (x) => x.emailAddress?.address?.toLowerCase() === this.currentUserEmailLowerCased()
                            );
                            return {
                              finished: anyResourceAttendeesFinished,
                              id: si.id,
                              meAttending: meAttending,
                              start: CalendarEventDatetime.fromBrowserDatetime(si.start),
                              end: CalendarEventDatetime.fromBrowserDatetime(si.end),
                              subject: si.subject,
                              showAs: si.showAs,
                              status: si.status,
                              locations: si.locations,
                              type: si.type,
                              attendees: si.attendees.map((a) => {
                                const foundInResourceAttendees = resourceAttendees.find(
                                  (rA) =>
                                    rA.attendee.emailAddress?.address.toUpperCase() ===
                                    a.emailAddress?.address.toUpperCase()
                                );
                                if (foundInResourceAttendees) {
                                  a.resource = true;
                                }
                                return a;
                              }),
                              cancelled: si.cancelled,
                              afterEnd: afterEnd,
                              canEditFromColleague: canEditFromColleague,
                              canViewFromColleague: canViewFromColleague,
                              showConfirmButton: showConfirmButton,
                              showChangeButton: showChangeButton,
                              showFinishButton: showFinishButton,
                              showDeleteButton: showDeleteButton,
                              bodyPreview: si.bodyPreview,
                              bodyHtml: si.bodyHtml,
                              iCalUId: si.iCalUId,
                              isOrganizer: si.isOrganizer,
                              ownerEventId: si.ownerEventId,
                              onlineMeetingProvider: si.onlineMeetingProvider,
                              onlineMeetingUrl:
                                si.onlineMeetingUrl ??
                                statuses.find(
                                  (status) => status.iCalUid === si.iCalUId && (status.id === si.id || !status.id)
                                )?.onlineMeetingUrl,
                              eventPermissions: si.eventPermissions,
                              visibility: si.visibility,
                              resourceAttendees: resourceAttendees,
                              reminder: si.reminder,
                              isAllDay: si.isAllDay,
                              organizer: si.organizer,
                              assistEventType: AssistEventType.Identified,
                              ownerUserId: si.ownerUserId,
                              seriesMasterId: si.seriesMasterId,
                              occurrenceId: si.occurrenceId,
                              recurrence: si.recurrence,
                              left: null,
                              width: null,
                            } satisfies TimelineIdentifiedAssistEvent;
                          } catch (error: any) {
                            // Proste se to nepovedlo
                            return null;
                          }
                        })
                        .filter((x) => !!x)
                    : [],
                  requirementId: fr.requirement.id,
                };
                return data;
              })
          );
        });
      })
    );
  }

  // eventy samotny
  getEventById(
    email: string,
    id: string,
    resources: AssistResourceBM[],
    occurrenceId?: string,
    ownerUserId?: string
  ): Observable<IdentifiedAssistEvent> {
    return this.calendarsService
      .getCalendarEventById(this.currentUserEmailLowerCased(), email, id, occurrenceId, ownerUserId)
      .pipe(
        switchMap((event) => this.getCalendarEventsFromIdentifiedAssistEvents([event], resources)),
        map((events) => events[0])
      );
  }

  getCurrentUserEmailLoweCased(): Observable<string> {
    return this.store.select(currentUser).pipe(
      filter((x) => x.loading === false && x.data?.email != null),
      map((x) => x.data.email.toLowerCase())
    );
  }

  getEventsForCollegue(identifier: string, from: Date, to: Date): Observable<IdentifiedAssistEvent[]> {
    return this.calendarsService.getCalendarEvents(this.currentUserEmailLowerCased(), from, to, identifier, null).pipe(
      concatLatestFrom((x) => this.store.select((x) => x.shared.resources).pipe(map((x) => x.data))),
      switchMap(([eventsResponse, resources]) =>
        this.getCalendarEventsFromIdentifiedAssistEvents(eventsResponse.events, resources)
      )
    );
  }

  getDashboardEvents(resources: AssistResourceBM[]): Observable<IdentifiedAssistEvent[]> {
    const getEventsFromToNextLink = (from: Date, to: Date, nextLink?: string): Observable<CalendarEvent[]> => {
      return this.calendarsService
        .getCalendarEvents(this.currentUserEmailLowerCased(), from, to, 'me', { nextLink: nextLink })
        .pipe(
          switchMap(({ events, nextLink }) => {
            if (nextLink != null) {
              return getEventsFromToNextLink(from, to, nextLink).pipe(map((x) => [...events, ...x]));
            } else {
              return of(events);
            }
          }),
          map((events) => {
            // only distinct and top 10
            return events.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
          })
        );
    };

    return getEventsFromToNextLink(new Date(), add(new Date(), { days: 30 })).pipe(
      switchMap((events) => this.getCalendarEventsFromIdentifiedAssistEvents(events, resources)),
      map((events) => {
        return events
          .filter((e) => {
            return !e.cancelled;
          })
          .sort((a, b) => {
            return a.start.utcDateTime < b.start.utcDateTime ? -1 : 1;
          })
          .slice(0, 10);
      })
    );
  }

  getMyEventsWhereIsResource(from: Date, to: Date, resources: AssistResourceBM[]): Observable<IdentifiedAssistEvent[]> {
    return this.calendarsService
      .getCalendarEvents(this.currentUserEmailLowerCased(), from, to, 'me', null)
      .pipe(switchMap((r) => this.getCalendarEventsFromIdentifiedAssistEvents(r.events, resources)));
  }

  getCalendarSyncTimelineEvents(
    from: Date,
    to: Date,
    resources: AssistResourceBM[],
    fallbackToEmptyOnError = true
  ): Observable<IdentifiedAssistEvent[]> {
    const resourceUsernames = [
      ...new Set(resources.map((resource) => resource.username).filter((username): username is string => !!username)),
    ];

    if (resourceUsernames.length === 0) {
      return of([]);
    }

    return this.calendarSyncService
      .loadCalendarEvents({
        startUtc: from.toISOString(),
        endUtc: to.toISOString(),
        group: false,
        resourceUsernames,
      })
      .pipe(
        switchMap((response) => this.calendarSyncCalendarsServiceResolver.ensureServicesForResponse(response)),
        map((response) => this.mapCalendarSyncTimelineResponseToCalendarEvents(response)),
        switchMap((events) => this.getCalendarEventsFromIdentifiedAssistEvents(events, resources)),
        catchError((error) => {
          console.error('Failed to load CalendarSync timeline events', error);
          return fallbackToEmptyOnError ? of([]) : throwError(() => error);
        })
      );
  }

  private mapCalendarSyncTimelineResponseToCalendarEvents(response: CalendarSyncLoadEventsResponse): CalendarEvent[] {
    const events = (response.resources ?? []).flatMap((resource) =>
      (resource.events ?? []).map((event) => this.withCalendarSyncResourceContext(event, resource))
    );

    return this.deduplicateCalendarSyncTimelineEvents(events)
      .map((event) => this.mapCalendarSyncEventToCalendarEvent(event))
      .filter((event): event is CalendarEvent => event != null);
  }

  private withCalendarSyncResourceContext(
    event: CalendarSyncCalendarEvent,
    resource: CalendarSyncResourceEvents
  ): CalendarSyncCalendarEvent {
    const resourceEmail = resource.email?.trim();
    const resourceUsername = resource.username?.trim();
    const resourceAddress = resourceEmail || resourceUsername;
    const resourceName = resourceUsername || resourceEmail || '';

    if (!resourceAddress) {
      return event;
    }

    const hasResourceAttendee = (event.attendees ?? []).some((attendee) => {
      const address = attendee.emailAddress?.address?.toLowerCase();
      return address === resourceEmail?.toLowerCase() || address === resourceUsername?.toLowerCase();
    });

    const hasResourceLocation = (event.locations ?? []).some((location) => {
      const identifiers = [location.uniqueId, location.locationUri, location.displayName].map((value) =>
        value?.toLowerCase()
      );
      return (
        identifiers.includes(resourceEmail?.toLowerCase()) || identifiers.includes(resourceUsername?.toLowerCase())
      );
    });

    return {
      ...event,
      providerKey: event.providerKey ?? resource.providerKey,
      serverTypeId: event.serverTypeId ?? resource.serverTypeId,
      attendees: hasResourceAttendee
        ? event.attendees
        : [
            ...(event.attendees ?? []),
            {
              type: 'resource',
              emailAddress: {
                address: resourceAddress,
                name: resourceName,
              },
              status: {
                response: 'accepted',
              },
            },
          ],
      locations: hasResourceLocation
        ? event.locations
        : [
            ...(event.locations ?? []),
            {
              displayName: resourceName,
              locationType: 'conferenceRoom',
              uniqueId: resourceAddress,
              uniqueIdType: 'email',
              locationUri: resourceAddress,
            },
          ],
    };
  }

  private deduplicateCalendarSyncTimelineEvents(events: CalendarSyncCalendarEvent[]): CalendarSyncCalendarEvent[] {
    const byKey = new Map<string, CalendarSyncCalendarEvent>();

    for (const event of events) {
      const key = this.getCalendarSyncTimelineEventKey(event);
      const existing = byKey.get(key);

      byKey.set(
        key,
        existing
          ? {
              ...existing,
              attendees: this.mergeCalendarSyncAttendees(existing.attendees, event.attendees),
              locations: this.mergeCalendarSyncLocations(existing.locations, event.locations),
            }
          : event
      );
    }

    return [...byKey.values()];
  }

  private getCalendarSyncTimelineEventKey(event: CalendarSyncCalendarEvent): string {
    const provider = event.providerKey ?? event.serverType ?? event.serverTypeId ?? '';
    const logicalId = event.iCalUId ?? event.id ?? '';
    const occurrence = event.occurrenceId ?? event.originalStart ?? event.startUtc ?? event.start?.dateTime ?? '';
    const start = event.startUtc ?? event.start?.dateTime ?? '';
    const end = event.endUtc ?? event.end?.dateTime ?? '';
    const organizer = event.organizer?.emailAddress?.address ?? '';

    return [provider, logicalId, occurrence, start, end, organizer]
      .map((part) => String(part).trim().toLowerCase())
      .join('|');
  }

  private mergeCalendarSyncAttendees(
    first: CalendarSyncCalendarEvent['attendees'],
    second: CalendarSyncCalendarEvent['attendees']
  ): CalendarSyncCalendarEvent['attendees'] {
    const byKey = new Map<string, CalendarSyncCalendarEvent['attendees'][number]>();

    for (const attendee of [...(first ?? []), ...(second ?? [])]) {
      const key = `${attendee.emailAddress?.address ?? ''}|${attendee.emailAddress?.name ?? ''}|${attendee.type ?? ''}`;
      byKey.set(key.toLowerCase(), attendee);
    }

    return [...byKey.values()];
  }

  private mergeCalendarSyncLocations(
    first: CalendarSyncCalendarEvent['locations'],
    second: CalendarSyncCalendarEvent['locations']
  ): CalendarSyncCalendarEvent['locations'] {
    const byKey = new Map<string, CalendarSyncCalendarEvent['locations'][number]>();

    for (const location of [...(first ?? []), ...(second ?? [])]) {
      const key = `${location.uniqueId ?? ''}|${location.locationUri ?? ''}|${location.displayName ?? ''}`;
      byKey.set(key.toLowerCase(), location);
    }

    return [...byKey.values()];
  }

  private mapCalendarSyncEventToCalendarEvent(event: CalendarSyncCalendarEvent): CalendarEvent | null {
    if (!event.iCalUId) {
      return null;
    }

    try {
      const allDayRange = event.isAllDay ? getCalendarSyncTimelineEventRange(event) : null;
      const start = allDayRange
        ? CalendarEventDatetime.fromBrowserDatetime(allDayRange.start)
        : this.mapCalendarSyncDateTime(event.start, event.startUtc);
      const end = allDayRange
        ? CalendarEventDatetime.fromBrowserDatetime(allDayRange.end)
        : this.mapCalendarSyncDateTime(event.end, event.endUtc);

      return {
        id: event.id ?? event.iCalUId,
        iCalUId: event.iCalUId,
        subject: event.subject ?? '',
        start,
        end,
        cancelled: event.isCancelled === true,
        organizer: this.mapCalendarSyncOrganizer(event.organizer),
        attendees: this.mapCalendarSyncAttendees(event.attendees),
        locations: this.mapCalendarSyncLocations(event),
        isOrganizer: false,
        showAs: this.mapCalendarSyncShowAs(event.showAs),
        status: event.responseStatus?.response ?? (event.isCancelled ? 'cancelled' : 'accepted'),
        bodyPreview: event.bodyPreview ?? event.body?.content ?? '',
        bodyHtml: event.body?.content ?? event.bodyPreview ?? '',
        ownerEventId: event.ownerEventId ?? undefined,
        ownerUserId: event.ownerUserId ?? undefined,
        seriesMasterId: event.seriesMasterId ?? undefined,
        occurrenceId: event.occurrenceId ?? undefined,
        recurrence: (event.recurrence as unknown as CalendarEventRecurrence | null) ?? null,
        originalStart: event.originalStart
          ? CalendarEventDatetime.fromBrowserDatetime(new Date(event.originalStart))
          : undefined,
        onlineMeetingProvider: event.onlineMeetingProvider ?? '',
        onlineMeetingUrl: (event.onlineMeeting?.['joinUrl'] as string | undefined) ?? event.onlineMeetingUrl,
        isAllDay: event.isAllDay === true,
        type: this.normalizeCalendarSyncEventType(event.type),
        visibility: 'public',
        reminder: {
          enabled: event.isReminderOn === true,
          minutesBeforeStart: event.reminderMinutesBeforeStart ?? 0,
        },
      };
    } catch {
      return null;
    }
  }

  private mapCalendarSyncDateTime(
    value: CalendarSyncCalendarEvent['start'],
    utcValue: string | null | undefined
  ): CalendarEventDatetime {
    const dateTime = value?.dateTime?.split('.')[0];
    const timeZone = value?.timeZone;

    if (dateTime && !timeZone) {
      return CalendarEventDatetime.fromLocalIANA(
        dateTime.includes('T') ? dateTime : `${dateTime}T00:00:00`,
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    }

    if (dateTime && timeZone) {
      try {
        return CalendarEventDatetime.fromLocalWindows(dateTime, timeZone);
      } catch {
        try {
          return CalendarEventDatetime.fromLocalIANA(dateTime, timeZone);
        } catch {
          if (utcValue) {
            return CalendarEventDatetime.fromBrowserDatetime(new Date(utcValue));
          }
        }
      }
    }

    if (utcValue) {
      return CalendarEventDatetime.fromBrowserDatetime(new Date(utcValue));
    }

    throw new Error('Missing CalendarSync event date');
  }

  private mapCalendarSyncOrganizer(value: CalendarSyncCalendarEvent['organizer']): CalendarOrganizer {
    return {
      emailAddress: {
        address: value?.emailAddress?.address ?? '',
        name: value?.emailAddress?.name ?? value?.emailAddress?.address ?? '',
      },
    };
  }

  private mapCalendarSyncAttendees(attendees: CalendarSyncCalendarEvent['attendees']): CalendarAttendee[] {
    return (attendees ?? []).map((attendee) => {
      const type = attendee.type?.toLowerCase() ?? 'required';

      return {
        required: type !== 'optional',
        type,
        resource: type === 'resource',
        status: {
          response: (attendee.status?.response ?? 'none') as CalendarAttendee['status']['response'],
          time: attendee.status?.time ?? '',
        },
        emailAddress: {
          address: attendee.emailAddress?.address ?? '',
          name: attendee.emailAddress?.name ?? attendee.emailAddress?.address ?? '',
        },
      };
    });
  }

  private mapCalendarSyncLocations(event: CalendarSyncCalendarEvent): CalendarLocation[] {
    const locations = [...(event.location ? [event.location] : []), ...(event.locations ?? [])];
    const byKey = new Map<string, CalendarLocation>();

    for (const location of locations) {
      const mappedLocation = {
        displayName: location.displayName ?? '',
        locationType: location.locationType ?? '',
        uniqueId:
          location.uniqueId ?? location.locationUri ?? location.locationEmailAddress ?? location.displayName ?? '',
        uniqueIdType: location.uniqueIdType ?? '',
        locationUri: location.locationUri ?? location.locationEmailAddress ?? location.uniqueId ?? '',
      };
      const key = `${mappedLocation.uniqueId}|${mappedLocation.locationUri}|${mappedLocation.displayName}`;
      byKey.set(key.toLowerCase(), mappedLocation);
    }

    return [...byKey.values()];
  }

  private mapCalendarSyncShowAs(value: string | null | undefined): { key: string; value: string } {
    const normalized = value?.toLowerCase() === 'free' ? 'free' : 'busy';
    return {
      key: normalized === 'free' ? 'reservationPopup.free' : 'reservationPopup.busy',
      value: normalized,
    };
  }

  private normalizeCalendarSyncEventType(value: string | null | undefined): CalendarEvent['type'] {
    switch (value) {
      case 'occurrence':
      case 'exception':
      case 'seriesMaster':
      case 'singleInstance':
        return value;
      default:
        return 'singleInstance';
    }
  }

  getReservationByICalIUid(
    from: Date,
    to: Date,
    resources: AssistResourceBM[],
    iCalUid: string
  ): Observable<IdentifiedAssistEvent> {
    return this.getReservations(from, to, resources, null, 'me', false, false).pipe(
      map(
        (response) =>
          response.events.find((x) => (x as IdentifiedAssistEvent).iCalUId == iCalUid) as IdentifiedAssistEvent
      )
    );
  }

  getReservationByICalIUidFromCalendar(
    from: Date,
    to: Date,
    resources: AssistResourceBM[],
    iCalUid: string,
    calendarEmail: string
  ): Observable<IdentifiedAssistEvent | undefined> {
    return this.getReservations(from, to, resources, null, calendarEmail, false, false).pipe(
      map((response) => response.events.find((x) => x.iCalUId == iCalUid))
    );
  }

  getOtherCalendars(): Observable<OtherCalendarListItem[]> {
    return this.apiService.call(otherCalendars());
  }

  updateOtherCalendar(otherCalendar: PostPutOtherCalendarListItem): Observable<OtherCalendarListItem> {
    return this.apiService.call(updateOtherCalendarRequest(otherCalendar));
  }

  searchOtherCalendar(search: string): Observable<OtherCalendarListItem[]> {
    return this.apiService.call(searchOtherCalendarRequest(search));
  }

  addOtherCalendar(otherCalendar: PostPutOtherCalendarListItem): Observable<OtherCalendarListItem> {
    return this.apiService.call(addOtherCalendarRequest(otherCalendar));
  }

  deleteOtherCalendar(otherCalendar: OtherCalendarListItem): Observable<OtherCalendarListItem> {
    return this.apiService.call(deleteOtherCalendarRequest(otherCalendar));
  }

  currentUserEmailLowerCased = toSignal(this.getCurrentUserEmailLoweCased());

  getReservations(
    from: Date,
    to: Date,
    resources: AssistResourceBM[],
    originalSettings: ReservationFilters | null,
    calendarEmail: string,
    isResource: boolean,
    returnLimited: true
  ): Observable<{ events: (IdentifiedAssistEvent | LimitedAssistEvent)[]; nextLink: string }>;

  getReservations(
    from: Date,
    to: Date,
    resources: AssistResourceBM[],
    originalSettings: ReservationFilters | null,
    calendarEmail: string,
    isResource: boolean,
    returnLimited: false
  ): Observable<{ events: IdentifiedAssistEvent[]; nextLink: string }>;

  getReservations(
    from: Date,
    to: Date,
    resources: AssistResourceBM[],
    originalSettings: ReservationFilters | null,
    calendarEmail: string,
    isResource: boolean,
    returnLimited: boolean
  ): Observable<{ events: (IdentifiedAssistEvent | LimitedAssistEvent)[]; nextLink: string }> {
    const settings: ReservationFilters = originalSettings ?? {
      filters: {
        state: [],
        showCanceled: true,
      },
      resourceNames: [],
      attendees: [],
    };
    return this.calendarsService
      .getCalendarEvents(this.currentUserEmailLowerCased(), from, to, calendarEmail, settings)
      .pipe(
        switchMap((r) =>
          this.getCalendarEventsFromIdentifiedAssistEvents(r.events, resources).pipe(
            map((identifiedEvents) => {
              const limitedEvents = r.events
                .filter((e) => !identifiedEvents.find((y) => y.id === e.id))
                .map((ev) => {
                  const limitedEvent: LimitedAssistEvent = {
                    assistEventType: AssistEventType.Limited,
                    start: ev.start,
                    end: ev.end,
                    subject: ev.subject,
                  };
                  return limitedEvent;
                });

              return {
                identifiedEvents: identifiedEvents,
                limitedEvents: limitedEvents,
                nextLink: r.nextLink,
                errorCode: r.errorCode,
                errorDescription: r.errorDescription,
              };
            })
          )
        ),
        map((x) => {
          const identified = this.filterEventsBySettings(
            x.identifiedEvents.filter(isIdentifiedAssistEvent),
            settings,
            resources
          );
          const limited = x.limitedEvents;
          const events = !returnLimited
            ? (identified as IdentifiedAssistEvent[])
            : ([...identified, ...limited] as (IdentifiedAssistEvent | LimitedAssistEvent)[]);

          return {
            events: events,
            nextLink: x.nextLink,
            errorCode: x.errorCode,
            errorDescription: x.errorDescription,
          };
        }),
        catchError((error) => {
          if (isResource && error.status === 404 && this.calendarsService.getCalendarEventsAlternative) {
            return this.calendarsService.getCalendarEventsAlternative(from, to, calendarEmail, settings).pipe(
              map((x) => ({
                ...x,
                events: x.events.map((e: any) => ({ ...e, assistEventType: AssistEventType.Limited })),
                nextLink: x.nextLink,
              }))
            );
          } else {
            throw error;
          }
        })
      );
  }
}
